import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Target, Clock, Calendar, Building2, ArrowRight, ArrowLeft, Sparkles, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useCompleteOnboarding } from "@/hooks/useOnboarding"
import { calculateIdealHourlyRate, TAX_REGIME_PRESETS, formatCurrency, TaxRegime } from "@/lib/calculator-calculations"

interface OnboardingWizardProps {
  onComplete: () => void
  onSkip: () => void
}

const steps = [
  { id: 'goal', icon: Target, title: 'Meta Mensal', description: 'Quanto você quer faturar por mês?' },
  { id: 'rate', icon: Clock, title: 'Valor Hora', description: 'Qual seu valor/hora atual?' },
  { id: 'hours', icon: Calendar, title: 'Disponibilidade', description: 'Quantas horas trabalha por semana?' },
  { id: 'regime', icon: Building2, title: 'Regime Tributário', description: 'Qual seu regime de trabalho?' },
]

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [showSummary, setShowSummary] = useState(false)
  const completeOnboarding = useCompleteOnboarding()

  // Form state
  const [monthlyGoal, setMonthlyGoal] = useState("")
  const [hourlyRate, setHourlyRate] = useState("")
  const [weeklyHours, setWeeklyHours] = useState("40")
  const [taxRegime, setTaxRegime] = useState<TaxRegime>("mei")

  const canProceed = () => {
    switch (currentStep) {
      case 0: return monthlyGoal && parseFloat(monthlyGoal) > 0
      case 1: return hourlyRate && parseFloat(hourlyRate) > 0
      case 2: return weeklyHours && parseFloat(weeklyHours) > 0
      case 3: return taxRegime
      default: return false
    }
  }

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      setShowSummary(true)
    }
  }

  const handleBack = () => {
    if (showSummary) {
      setShowSummary(false)
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleComplete = async () => {
    await completeOnboarding.mutateAsync({
      monthly_goal: parseFloat(monthlyGoal),
      hourly_rate: parseFloat(hourlyRate),
      weekly_work_hours: parseInt(weeklyHours),
      tax_regime: taxRegime,
    })
    onComplete()
  }

  // Calculate summary metrics
  const preset = TAX_REGIME_PRESETS[taxRegime]
  const result = calculateIdealHourlyRate({
    monthlyGoal: parseFloat(monthlyGoal) || 0,
    weeklyWorkHours: parseFloat(weeklyHours) || 40,
    weeksPerMonth: 4,
    taxPercentage: preset.taxPercentage,
    vacationPercentage: preset.vacationPercentage,
    thirteenthPercentage: preset.thirteenthPercentage,
    fixedMonthlyCosts: 0,
    taxRegime,
  })

  const maxCapacity = (parseFloat(weeklyHours) || 40) * 4 * (parseFloat(hourlyRate) || 0)
  const gap = (parseFloat(monthlyGoal) || 0) - maxCapacity

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <Card className="w-full sm:max-w-lg shadow-2xl border-0 sm:border-2 rounded-t-3xl sm:rounded-2xl max-h-[90vh] sm:max-h-none overflow-y-auto">
        <CardHeader className="relative pt-6 sm:pt-6 pb-4">
          {/* Mobile drag indicator */}
          <div className="sm:hidden absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-muted rounded-full" />
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-3 top-3 sm:right-4 sm:top-4"
            onClick={onSkip}
          >
            <X className="h-4 w-4" />
          </Button>

          {!showSummary && (
            <>
              {/* Progress */}
              <div className="flex gap-1.5 sm:gap-2 mb-4 mt-2 sm:mt-0">
                {steps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1 sm:h-1.5 flex-1 rounded-full transition-colors ${
                      idx <= currentStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = steps[currentStep].icon
                  return <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary shrink-0" />
                })()}
                <div className="min-w-0">
                  <CardTitle className="text-lg sm:text-xl">{steps[currentStep].title}</CardTitle>
                  <CardDescription className="text-sm">{steps[currentStep].description}</CardDescription>
                </div>
              </div>
            </>
          )}

          {showSummary && (
            <div className="flex items-center gap-3 mt-2 sm:mt-0">
              <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-primary shrink-0" />
              <div className="min-w-0">
                <CardTitle className="text-lg sm:text-xl">Resumo da Configuração</CardTitle>
                <CardDescription className="text-sm">Veja seu potencial financeiro</CardDescription>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="pb-6 sm:pb-6">
          <AnimatePresence mode="wait">
            {!showSummary ? (
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {currentStep === 0 && (
                  <div className="space-y-4">
                    <Label htmlFor="goal">Meta de faturamento mensal (R$)</Label>
                    <Input
                      id="goal"
                      type="number"
                      placeholder="10000"
                      value={monthlyGoal}
                      onChange={(e) => setMonthlyGoal(e.target.value)}
                      className="text-lg h-12"
                      autoFocus
                    />
                    <p className="text-sm text-muted-foreground">
                      Quanto você deseja faturar por mês para atingir seus objetivos financeiros?
                    </p>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="space-y-4">
                    <Label htmlFor="rate">Seu valor/hora atual (R$)</Label>
                    <Input
                      id="rate"
                      type="number"
                      placeholder="100"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      className="text-lg h-12"
                      autoFocus
                    />
                    <p className="text-sm text-muted-foreground">
                      Se não sabe, coloque uma estimativa. Vamos calcular o valor ideal para você.
                    </p>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-4">
                    <Label htmlFor="hours">Horas disponíveis por semana</Label>
                    <Input
                      id="hours"
                      type="number"
                      placeholder="40"
                      value={weeklyHours}
                      onChange={(e) => setWeeklyHours(e.target.value)}
                      className="text-lg h-12"
                      autoFocus
                    />
                    <p className="text-sm text-muted-foreground">
                      Considere apenas horas que podem ser vendidas para clientes.
                    </p>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-3">
                    <Label>Regime de trabalho</Label>
                    <RadioGroup value={taxRegime} onValueChange={(v) => setTaxRegime(v as TaxRegime)} className="space-y-2">
                      {Object.entries(TAX_REGIME_PRESETS).map(([key, preset]) => (
                        <div key={key} className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                          <RadioGroupItem value={key} id={key} className="mt-0.5" />
                          <label htmlFor={key} className="flex-1 cursor-pointer">
                            <div className="font-medium text-sm sm:text-base">{key.toUpperCase().replace('_', ' ')}</div>
                            <div className="text-xs sm:text-sm text-muted-foreground">{preset.description}</div>
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="summary"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                <div className="grid gap-3">
                  <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border">
                    <div className="text-xs sm:text-sm text-muted-foreground">Capacidade Máxima Mensal</div>
                    <div className="text-xl sm:text-2xl font-bold text-primary">{formatCurrency(maxCapacity)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {weeklyHours}h/semana × 4 semanas × {formatCurrency(parseFloat(hourlyRate) || 0)}/h
                    </div>
                  </div>

                  <div className={`p-3 sm:p-4 rounded-xl border ${gap > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {gap > 0 ? 'Gap para Meta' : 'Margem Disponível'}
                    </div>
                    <div className={`text-xl sm:text-2xl font-bold ${gap > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {formatCurrency(Math.abs(gap))}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {gap > 0 
                        ? 'Ajuste valor/hora ou horas para atingir a meta'
                        : 'Sua capacidade supera a meta mensal'
                      }
                    </div>
                  </div>

                  <div className="p-3 sm:p-4 rounded-xl bg-muted/50 border">
                    <div className="text-xs sm:text-sm text-muted-foreground">Valor/Hora Ideal Sugerido</div>
                    <div className="text-xl sm:text-2xl font-bold">{formatCurrency(result.idealHourlyRate)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Baseado na sua meta, horas e regime
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Use a Calculadora Profissional para ajustar esses valores a qualquer momento.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 mt-6">
            {(currentStep > 0 || showSummary) && (
              <Button variant="outline" onClick={handleBack} className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            )}

            {!showSummary ? (
              <Button 
                onClick={handleNext} 
                disabled={!canProceed()}
                className="flex-1"
              >
                {currentStep === steps.length - 1 ? 'Ver Resumo' : 'Próximo'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleComplete}
                disabled={completeOnboarding.isPending}
                className="flex-1"
              >
                {completeOnboarding.isPending ? 'Salvando...' : 'Ir para o Dashboard'}
                <Sparkles className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>

          <Button 
            variant="link" 
            className="w-full mt-2 text-muted-foreground"
            onClick={onSkip}
          >
            Pular configuração inicial
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
